import { Fragment, useEffect, useMemo, useState } from "react";
import { Eye, Info, MoreHorizontal, Plus, SquareArrowOutUpRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { PartialSkill, SkillScope } from "~/services/skills";
import { sortSkills } from "./skillSort";

interface SkillsListProps {
  skills: PartialSkill[];
  // Filters the list by name or description (case-insensitive). Supplied by the
  // toolbar search box or by the text typed after "/" in the composer.
  searchFilter?: string;
  // Invoked with the bare skill name (no slash) when a skill is chosen.
  onSelectSkill: (skillName: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skill: PartialSkill) => void;
  // Deletes a USER skill. Only USER skills are editable/deletable here; AGENT
  // and DATASET skills are use-only and expose a read-only view instead.
  onDeleteSkill?: (skill: PartialSkill) => void;
  // Opens a read-only view of a non-USER (AGENT/DATASET) skill. USER skills are
  // viewed via their Edit action, so they get no separate view icon.
  onViewSkill?: (skill: PartialSkill) => void;
  // Maps a DATASET skill's scope_id → its dataset display name, used for the
  // "registered from" info tooltip next to dataset skills.
  datasetNames?: Record<string, string>;
  onLearnMore?: () => void;
  // Drives arrow-key navigation and Enter/Tab selection via a window listener.
  // Used by the "/" overlay; the toolbar relies on click/search instead.
  enableKeyboardNav?: boolean;
  onClose?: () => void;
}

function filterSkills(skills: PartialSkill[], searchText: string): PartialSkill[] {
  const search = searchText.trim().toLowerCase();
  if (!search) return skills;
  return skills.filter(
    (skill) =>
      skill.name.toLowerCase().includes(search) || skill.description.toLowerCase().includes(search)
  );
}

// Section heading shown above each scope group. Skills arrive pre-sorted by
// scope (see sortSkills), so equal-scope skills are always contiguous. The
// conversation-scoped endpoint only returns USER/AGENT/DATASET skills.
function sectionLabel(scope: SkillScope): string {
  switch (scope) {
    case "AGENT":
      return "Agent skills";
    case "DATASET":
      return "Dataset skills";
    case "USER":
      return "Your skills";
    default:
      return scope;
  }
}

// Only USER skills are manageable from the picker: they're yours to edit and
// delete (three-dot menu). AGENT and DATASET skills are use-only, so they get a
// read-only view (eye icon) instead. Clicking any row inserts the skill
// regardless of scope.
type NavItem = { kind: "skill"; skill: PartialSkill } | { kind: "add" } | { kind: "help" };

// Shared placement for the per-row trailing action button (three-dot / eye):
// hover-revealed, vertically centered at the right edge.
const ROW_ACTION_BUTTON_CLASS =
  "absolute right-2 top-1/2 size-6 -translate-y-1/2 text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

export const SkillsList = ({
  skills,
  searchFilter = "",
  onSelectSkill,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
  onViewSkill,
  datasetNames = {},
  onLearnMore,
  enableKeyboardNav = false,
  onClose,
}: SkillsListProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredSkills = useMemo(
    () => sortSkills(filterSkills(skills, searchFilter)),
    [skills, searchFilter]
  );

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = filteredSkills.map((skill) => ({ kind: "skill", skill }));
    if (onAddSkill) items.push({ kind: "add" });
    if (onLearnMore) items.push({ kind: "help" });
    return items;
  }, [filteredSkills, onAddSkill, onLearnMore]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [navItems.length]);

  useEffect(() => {
    if (!enableKeyboardNav || navItems.length === 0) return;

    const activate = (item: NavItem) => {
      if (item.kind === "skill") {
        onSelectSkill(item.skill.name);
      } else if (item.kind === "add") {
        onAddSkill?.();
        onClose?.();
      } else {
        onLearnMore?.();
        onClose?.();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % navItems.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + navItems.length) % navItems.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const item = navItems[selectedIndex];
        if (item) activate(item);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enableKeyboardNav, navItems, selectedIndex, onSelectSkill, onAddSkill, onLearnMore, onClose]);

  const hasFooter = Boolean(onAddSkill || onLearnMore);
  // Footer indices follow the skills in navItems, in render order.
  const addIndex = filteredSkills.length;
  const helpIndex = filteredSkills.length + (onAddSkill ? 1 : 0);

  return (
    <div className="flex max-h-[425px] flex-col overflow-y-auto pb-1" role="list">
      {filteredSkills.length > 0 ? (
        filteredSkills.map((skill, index) => {
          const startsSection = index === 0 || filteredSkills[index - 1].scope !== skill.scope;
          const isOwned = skill.scope === "USER";
          const hasActions = isOwned && Boolean(onEditSkill || onDeleteSkill);
          const showView = !isOwned && Boolean(onViewSkill);
          const datasetName =
            skill.scope === "DATASET" && skill.scope_id ? datasetNames[skill.scope_id] : undefined;
          return (
            <Fragment key={skill.id}>
              {startsSection && (
                <p className="sticky top-0 z-10 border-b border-border bg-popover px-3 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {sectionLabel(skill.scope)}
                </p>
              )}
              <div role="listitem" className="group relative">
                <button
                  type="button"
                  onClick={() => onSelectSkill(skill.name)}
                  className={cn(
                    "flex w-full cursor-pointer flex-col items-start gap-0.5 px-3 py-1.5 pr-9 text-left",
                    "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                    enableKeyboardNav && selectedIndex === index && "bg-muted"
                  )}
                >
                  <span className="flex items-center gap-1">
                    <span className="font-mono text-xs font-semibold text-foreground">
                      /{skill.name}
                    </span>
                    {datasetName && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex cursor-help"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Info
                              className="size-3 text-muted-foreground"
                              aria-label={datasetName}
                            />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{datasetName}</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                  <span className="line-clamp-2 text-xs text-muted-foreground">
                    {skill.description}
                  </span>
                </button>

                {hasActions && (
                  // modal={false} so the menu coexists with the parent Popover /
                  // composer overlay focus scope instead of being closed by it.
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(ROW_ACTION_BUTTON_CLASS, "data-[state=open]:opacity-100")}
                        aria-label={`Actions for ${skill.name}`}
                      >
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                      {onEditSkill && (
                        <DropdownMenuItem
                          onClick={() => {
                            onEditSkill(skill);
                            onClose?.();
                          }}
                        >
                          Edit
                        </DropdownMenuItem>
                      )}
                      {onDeleteSkill && (
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => {
                            onDeleteSkill(skill);
                            onClose?.();
                          }}
                        >
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {showView && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      onViewSkill?.(skill);
                      onClose?.();
                    }}
                    className={ROW_ACTION_BUTTON_CLASS}
                    aria-label={`View ${skill.name}`}
                  >
                    <Eye className="size-4" />
                  </Button>
                )}
              </div>
            </Fragment>
          );
        })
      ) : (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {skills.length === 0
            ? "No skills available for this conversation."
            : `No skills match “${searchFilter}”`}
        </p>
      )}

      {hasFooter && (
        <>
          {filteredSkills.length > 0 && <div className="my-1 border-t border-border" />}
          {onAddSkill && (
            <button
              type="button"
              onClick={() => {
                onAddSkill();
                onClose?.();
              }}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-foreground",
                "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                enableKeyboardNav && selectedIndex === addIndex && "bg-muted"
              )}
            >
              <Plus className="size-3.5 text-muted-foreground" />
              Add New Skill…
            </button>
          )}
          {onLearnMore && (
            <button
              type="button"
              onClick={() => {
                onLearnMore();
                onClose?.();
              }}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-foreground",
                "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
                enableKeyboardNav && selectedIndex === helpIndex && "bg-muted"
              )}
            >
              <SquareArrowOutUpRight className="size-3.5 text-muted-foreground" />
              Learn about AI Skills
            </button>
          )}
        </>
      )}
    </div>
  );
};
